'use client';
import { useRef, useEffect, useState } from 'react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ImageProps, ToutContentProps } from '@repo/ui/src/types';
import { useIsMD, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { useIntersectionObserver } from '@repo/ui/src/hooks/useIntersectionObserver';

export interface CardProps {
  backgroundImage: ImageProps;
  toutContent: ToutContentProps;
}

const CalloutSliderCard = ({ toutContent, backgroundImage }: CardProps) => {
  const backgroundImageUrl = buildImageSrc(backgroundImage, 800, 1, 1);
  const ref = useRef<HTMLDivElement | null>(null);
  const isMed = useIsMD();
  const reduceMotion = useReducedMotion();
  const [translateHeight, setHeight] = useState(0);
  const [visible, setVisible] = useState(false);
  const intersection = useIntersectionObserver(ref, { threshold: 0.8 });

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const bottomContent = element.querySelector('.bottom-content');
      const bottomContentHeight = bottomContent?.clientHeight;
      if (bottomContentHeight) {
        setHeight(bottomContentHeight);
      }
    }
  }, []);

  useEffect(() => {
    if (intersection?.isIntersecting) {
      setVisible(true);
    }
  }, [intersection]);

  return (
    <div
      ref={ref}
      className="callout-slider-item relative h-[85vh] max-h-[800px] flex flex-col overflow-hidden min-h-[500px] mb-6 focus-visible:outline-4 focus-visible:outline-brand-500 md:w-[33.33%] md:px-[.8vw] md:mb-0 md:hover:w-[85vh] md:hover:flex-shrink-0 md:hover:motion-reduce:w-[33.33%] group/card transition-all duration-700 ease-transitionQuad"
      tabIndex={0}
      data-testid="callout-slider-card"
    >
      <LazyLoadImage
        className="overflow-hidden h-full"
        imgComponent={
          <img
            src={backgroundImageUrl}
            role="presentation"
            alt=""
            className="w-full h-full object-cover object-left-center overflow-hidden"
          />
        }
      />
      <div className="bg-calloutMobile md:bg-calloutMedium lg:bg-calloutLarge absolute z-10 top-0 bottom-0 left-0 md:left-[.8vw] right-0 md:right-[.8vw]">
        <ToutContent
          dark
          divider
          narrowContent
          align="left"
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          {...toutContent}
          headingStyleVariant="h2"
          style={{
            transform: !reduceMotion && isMed && `translateY(${translateHeight + 70}px)`,
          }}
          className={classNames(
            'callout-slider-content absolute z-10 top-0 left-0 bottom-0 flex flex-col justify-end',
            'right-[0.5px] p-6 md:pb-[3.5rem] md:mx-[0.8vw] lg:p-[3.5rem] transition-all duration-700',
            'sm:[&_.heading]:pb-12',
            isMed
              ? `group-hover:duration-700 ease-in-out group-hover:opacity-0 content
                group-hover/card:[&.content]:opacity-100 group-hover/card:[&_.bottom-content]:opacity-100
                group-hover/card:[&_.bottom-content]:duration-700 [&_.bottom-content]:transition-opacity
                group-hover/card:[&_.bottom-content]:ease-in group-focus-visible/card:opacity-100 group-hover/card:!translate-y-0`
              : visible
                ? 'opacity-100'
                : 'opacity-0',
          )}
        />
      </div>
    </div>
  );
};

export default CalloutSliderCard;
